/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */

import "../UploadOrDecline.scss";

import { ActionType, EventName } from "@microsoftit/telemetry-extensions-npm";
import {
  ApplicationStatus,
  TelemetryHistoryLogs,
  TelemetryLogs,
} from "../../constants";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Link,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import React, { useState } from "react";

import { applicationApi } from "../../../services/application-apis/application-apis";
import { logTelemetry } from "../../../services/telemetry/telemetryWrapper";
import { uploadDeclineDialogStyle } from "../UploadOrDecline.styles";
import { triggerReferenceHistory } from "../../reference-history/reference-middle-layer";
import floodgateService from "../../../services/survey/FloodgateService";

interface Props {
  hideDeclineDialog: boolean;
  toggleDeclineDialog: () => void;
  setStatus: (status: ApplicationStatus) => void;
  candidateFirstName: string;
}

const declineDialogContent = {
  type: DialogType.normal,
  title: "Are you sure you want to decline?",
  closeButtonAriaLabel: "Close",
  subText: "",
};

const dialogStyles = { main: uploadDeclineDialogStyle };
const modalProps = {
  isBlocking: true,
  styles: dialogStyles,
};

export const DeclineDialog: React.FC<Props> = ({
  hideDeclineDialog,
  toggleDeclineDialog,
  setStatus,
  candidateFirstName,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeclineFailed, setIsDeclineFailed] = useState(false);

  declineDialogContent.subText = `We’ll let ${candidateFirstName} know that you have declined.`;

  const handleUploadRejection = async () => {
    setIsProcessing(true);
    try {
      await applicationApi.declineRequest();
      logTelemetry(
        TelemetryLogs.DelineRequest,
        TelemetryLogs.DelineRequest,
        ActionType.User,
        TelemetryLogs.DelineRequest,
        EventName.ButtonClick
      );
      setStatus(ApplicationStatus.DECLINED);
      toggleDeclineDialog();
      triggerReferenceHistory(TelemetryHistoryLogs.DeclineReference);
      await floodgateService.startEngine();
      floodgateService.triggerRequestCompletedActivity();
    } catch (e) {
      setIsDeclineFailed(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const onDismissingDeclineDialog = () => {
    setIsProcessing(false);
    setIsDeclineFailed(false);
    toggleDeclineDialog();
  };
  return (
    <Dialog
      hidden={hideDeclineDialog}
      onDismiss={onDismissingDeclineDialog}
      dialogContentProps={declineDialogContent}
      modalProps={modalProps}
    >
      <DialogFooter>
        {isProcessing ? (
          <Spinner
            size={SpinnerSize.medium}
            className="ms-ac-referenceview-decline-loader"
          />
        ) : (
          <></>
        )}
        <PrimaryButton
          onClick={handleUploadRejection}
          text={isProcessing ? "Declining" : "Decline"}
          disabled={isProcessing}
        />
        <DefaultButton onClick={onDismissingDeclineDialog} text="Cancel" />
        {isDeclineFailed && (
          <div className="ms-ac-referenceview-decline-custom-footer">
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={false}
              dismissButtonAriaLabel="Close"
              truncated={false}
            >
              Sorry, we are unable to submit your response now. Please refresh
              and try again. If the issue persists,
              <Link
                href={process.env.REACT_APP_CAREERS_PORTAL_SUPPORT_URI}
                target="_blank"
                underline
              >
                contact support.
              </Link>
            </MessageBar>
          </div>
        )}
      </DialogFooter>
    </Dialog>
  );
};
